import { Injectable } from '@angular/core';
import { interval, Subscription, Subject, firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ApiService } from '../../api.service';
import { FrontendLoggerService } from '../../frontend-logger.service';

interface CsvTask {
  taskId: string;
  status: 'pending' | 'ok' | 'error';
}

export interface CsvTaskSystemMessage {
  text: string;
  taskId?: string;
  resultUrl?: string;
  resultLabel?: string;
}

@Injectable({ providedIn: 'root' })
export class CsvTaskPollingService {
  private tasks: CsvTask[] = [];
  private pollingSub: Subscription | null = null;
  private readonly pollingInterval = 15000;
  public systemMessage$ = new Subject<CsvTaskSystemMessage>();

  constructor(private apiService: ApiService, private frontendLogger: FrontendLoggerService) {}

  addTask(taskId: string) { 
    if (!this.tasks.find(t => t.taskId === taskId)) {
      this.tasks.push({ taskId, status: 'pending' });
      if (!this.pollingSub) {
        this.startPolling();
      }
    }
  }

  private startPolling() {
    this.pollingSub = interval(this.pollingInterval)
      .pipe(
        switchMap(() => this.pollAllTasks())
      )
      .subscribe();
  }

  private pollAllTasks() {
    const pendingTasks = this.tasks.filter(t => t.status === 'pending' || t.status === 'error');
    return Promise.all(pendingTasks.map(task => this.pollTask(task)));
  }

  private async pollTask(task: CsvTask) {
    try {
        this.frontendLogger.debug(`Iniciando polling para task_id: ${task.taskId}`, 'CsvTaskPollingService.pollTask');
        const result: any = await firstValueFrom(this.apiService.get<any>(`csv/query_csv/result/${task.taskId}`));
        this.frontendLogger.info(`[Polling] Resultado para task_id: ${task.taskId}: ${JSON.stringify(result)}`, 'CsvTaskPollingService.pollTask');
        if (result?.status === 'ok' && result?.result_url) {
        task.status = 'ok';
        this.frontendLogger.info(`Resultado disponible para task_id: ${task.taskId}`, 'CsvTaskPollingService.pollTask');
        this.systemMessage$.next({
          text: `El resultado del task_id ${task.taskId} está disponible.`,
          taskId: task.taskId,
          resultUrl: result.result_url,
          resultLabel: 'Ver resultado'
        });
      } else if (result?.status === 'error') {
        task.status = 'error';
        this.frontendLogger.error(`Error en polling para task_id: ${task.taskId}`, 'CsvTaskPollingService.pollTask');
      }
    } catch (e) {
      this.frontendLogger.error(`[Polling] Error al obtener resultado para task_id: ${task.taskId}: ${e}`, 'CsvTaskPollingService.pollTask');
      // Si hay error de red, no cambia el estado, sigue intentando
    }
  }
}
